import { createAsyncThunk } from '@reduxjs/toolkit';
import HttpError from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import {
  returnAxiosOrGeneralFetchError,
  returnAxiosValidationOrGeneralFetchError,
} from '../../services/data/axios-base';
import Robot from '../../models/robots/robot';
import FormRobot from '../../models/robots/form-robot';
import organizationsDataService from '../../services/data/organizations-data-service';
import Mission from '../../models/missions/mission';
import Filters from '@common/enums/query-params/filters';
import RobotInformation from '../../models/robots/robot-information';
import DailyCleanedArea from '../../models/robots/daily-cleaned-area';
import { DateTime } from 'luxon';
import RobotAuthentication from '../../models/robots/robot-authentication';
import TorizonStatus from '../../models/robots/torizon-status';
import TorizonUpdateRequest from '../../models/robots/torizon-update-request';
import PaginationWrapper from '../../models/misc/api/pagination-wrapper';
import MissionList from '../../models/missions/mission-list';
import { DeploymentDetails } from '../../models/organization/deployment';
import RobotContact from '../../models/robots/robot-contact';
import LocalRobotDataState from 'models/robots/local-robot-data-state';
import RobotTag from 'models/robots/robot-tag';
import { DeploymentBlock } from 'utils/deployment-timeline/layout';
import { MissionReassignment } from '../../models/missions/mission-outside-range';

export const getOrganizationRobots = createAsyncThunk<
  PaginationWrapper<Robot>,
  Filters,
  { rejectValue: HttpError }
>('organizations/get-robots', async (filters: Filters, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getOrganizationRobotsById(filters);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getRobotById = createAsyncThunk<RobotInformation, string, { rejectValue: HttpError }>(
  'robots/retrieve',
  async (id: string, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getRobotById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getDailyCleanedAreaById = createAsyncThunk<
  DailyCleanedArea,
  { id: string; year?: number },
  { rejectValue: HttpError }
>(
  'robots/get-daily-cleaned-area',
  async (data: { id: string; year?: number }, { rejectWithValue }) => {
    const year = data.year ?? DateTime.now().year;

    try {
      return await organizationsDataService.getDailyCleanedAreaById(
        data.id,
        (data.year ?? year).toString(),
      );
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const createRobot = createAsyncThunk<
  Robot,
  Partial<FormRobot>,
  { rejectValue: HttpError | ValidationResult[] }
>('robots/post', async (data: Partial<FormRobot>, { rejectWithValue }) => {
  try {
    return await organizationsDataService.createRobot(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchRobotAuthenticationSettingsById = createAsyncThunk<
  RobotAuthentication,
  { id: string; data: Partial<RobotAuthentication> },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'robots/patch/authentication-settings',
  async (data: { id: string; data: Partial<RobotAuthentication> }, { rejectWithValue }) => {
    try {
      return await organizationsDataService.patchRobotAuthenticationSettingsById(
        data.id,
        data.data,
      );
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const patchRobotById = createAsyncThunk<
  RobotInformation,
  { id: string; data: Partial<FormRobot> },
  { rejectValue: HttpError | ValidationResult[] }
>('robots/patch', async (data: { id: string; data: Partial<FormRobot> }, { rejectWithValue }) => {
  try {
    return await organizationsDataService.patchRobotById(data.id, data.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteRobotById = createAsyncThunk<
  string,
  string,
  { rejectValue: HttpError | ValidationResult[] }
>('robots/delete', async (id, { rejectWithValue }) => {
  try {
    return await organizationsDataService.deleteRobotById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getMissions = createAsyncThunk<
  PaginationWrapper<MissionList>,
  Filters,
  { rejectValue: HttpError }
>('missions/get', async (filters: Filters, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getMissions(filters);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getMissionById = createAsyncThunk<Mission, string, { rejectValue: HttpError }>(
  'missions/retrieve',
  async (id: string, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getMissionById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getWireguardConfigById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'robots/get-wireguard-config',
  async (id: string, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getWireguardConfigById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getTorizonStatus = createAsyncThunk<TorizonStatus, string, { rejectValue: HttpError }>(
  'robots/torizon-status/get',
  async (id, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getTorizonStatusById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const postTorizonUpdate = createAsyncThunk<
  TorizonUpdateRequest,
  string,
  { rejectValue: HttpError }
>('robots/torizon-update/post', async (id, { rejectWithValue }) => {
  try {
    return await organizationsDataService.postTorizonUpdateById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const postTorizonUpdateCancel = createAsyncThunk<void, string, { rejectValue: HttpError }>(
  'robots/torizon-update-cancel/post',
  async (id, { rejectWithValue }) => {
    try {
      return await organizationsDataService.postTorizonUpdateCancelById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getDeploymentsByRobotId = createAsyncThunk<
  DeploymentDetails[],
  string,
  { rejectValue: HttpError }
>('robots/get-deployments', async (id: string, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getDeploymentsByRobotId(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchDeploymentByRobotId = createAsyncThunk<
  DeploymentDetails,
  { robotId: string; data: Partial<DeploymentDetails> },
  { rejectValue: HttpError }
>('robots/patch-deployment', async (payload, { rejectWithValue }) => {
  try {
    return await organizationsDataService.patchDeploymentByRobotId(payload.robotId, payload.data);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchDeploymentDatesByRobotId = createAsyncThunk<
  DeploymentDetails[],
  { robotId: string; updates: DeploymentBlock[] },
  { rejectValue: HttpError }
>('robots/patch-deployment-dates', async (payload, { rejectWithValue }) => {
  try {
    return await organizationsDataService.patchDeploymentDatesByRobotId(
      payload.robotId,
      payload.updates,
    );
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const reassignMissionsByRobotId = createAsyncThunk<
  DeploymentDetails[],
  { robotId: string; reassignments: MissionReassignment[] },
  { rejectValue: HttpError }
>('robots/reassign-missions', async (payload, { rejectWithValue }) => {
  try {
    return await organizationsDataService.reassignMissionsByRobotId(
      payload.robotId,
      payload.reassignments,
    );
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getContactsByRobotId = createAsyncThunk<
  RobotContact[],
  string,
  { rejectValue: HttpError }
>('robots/get-contacts', async (id: string, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getContactsByRobotId(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getLocalRobotDataStateById = createAsyncThunk<
  LocalRobotDataState,
  string,
  { rejectValue: HttpError }
>('robots/get-local-data-state', async (id, { rejectWithValue }) => {
  try {
    return await organizationsDataService.getLocalRobotDataStateById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getTags = createAsyncThunk<string[], void, { rejectValue: HttpError }>(
  'tags/get',
  async (_, { rejectWithValue }) => {
    try {
      return await organizationsDataService.getTags();
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const postTag = createAsyncThunk<
  RobotTag,
  Omit<RobotTag, 'id' | 'dateCreated'>,
  { rejectValue: HttpError | ValidationResult[] }
>('tags/post', async (tag, { rejectWithValue }) => {
  try {
    return await organizationsDataService.postTag(tag);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchTagById = createAsyncThunk<
  RobotTag,
  { id: string; data: Partial<RobotTag> },
  { rejectValue: HttpError | ValidationResult[] }
>('tags/patch', async (payload, { rejectWithValue }) => {
  try {
    return await organizationsDataService.patchTagById(payload.id, payload.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteTagById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'tags/delete',
  async (id, { rejectWithValue }) => {
    try {
      return await organizationsDataService.deleteTagById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);
